import * as React from "react";
import { Icon, IIconProps } from "./Icon";
import { AnimatePresence, motion } from "framer-motion";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import * as Portal from "@radix-ui/react-portal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  agregarToken,
  cerrarMenu,
  setRoles,
  agregarAvatar,
} from "../../features/ui/uiSlice";
import logo from "/images/amex_insuranceservices_logo_h.png";
import { useGetPermisosQuery } from "../../services/docs";
import { Spinner } from "../Spinner";
import { useAuth } from "react-oidc-context";

// ---
// Interfaces
// ---
export interface IMenuItem {
  fc_menu: string;
  fc_icon: IIconProps["variante"];
  fc_ruta: string;
  submenus?: IMenuItem[];
}

export interface IMenuProps {
  items: IMenuItem[];
}

// ---
// Contenedor Principal del Menú
// ---
const Menu = (): JSX.Element => {
  const { data, isLoading, error } = useGetPermisosQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 1. Instanciamos auth para tener acceso a Keycloak
  const auth = useAuth();
  const isSSO = import.meta.env.VITE_SSO === "true";

  // 2. Efecto para manejar el Error 401 (Unauthorized)
  React.useEffect(() => {
    if (error && "status" in error && error.status === 401) {
      console.warn("Sesión expirada (401). Cerrando sesión...");

      // Limpiamos el token de nuestra tienda de Redux
      dispatch(agregarToken(""));

      if (isSSO) {
        // Flujo Keycloak: Borra la data local del usuario y redirige a Keycloak
        auth.removeUser().then(() => {
          auth.signinRedirect();
        });
      } else {
        // Flujo Tradicional: Redirige al login local
        navigate("/auth/login", { replace: true });
      }
    }
  }, [error, dispatch, navigate, auth, isSSO]);

  // 3. Efecto para guardar permisos en Redux
  React.useEffect(() => {
    if (data) {
      dispatch(agregarAvatar(data.fc_avatar));
      dispatch(setRoles(data.roles));
    }
  }, [data, dispatch]);

  const menuItems = data?.menu || [];

  return (
    <>
      {isLoading ? (
        <aside className="z-20 flex w-72 flex-shrink-0 items-center justify-center border-r border-slate-200/90 bg-white shadow-[2px_0_12px_rgba(0,0,0,0.02)]">
          <Spinner />
        </aside>
      ) : (
        <>
          <MenuEscritorio items={menuItems} />
          <MenuMovil items={menuItems} />
        </>
      )}
    </>
  );
};
// ---
// Menú de Escritorio (Sidebar Estático)
// ---
export const MenuEscritorio = ({ items }: IMenuProps): JSX.Element => {
  const { menu: isExpanded } = useSelector((state: RootState) => state.ui);
  const dispatch = useDispatch();

  const toggleMenu = () => {
    dispatch(cerrarMenu());
  };

  return (
    <aside
      className={`z-20 hidden flex-shrink-0 flex-col border-r border-slate-200/90 bg-white shadow-[2px_0_12px_rgba(0,0,0,0.02)] transition-all duration-300 md:flex
        ${isExpanded ? "w-72" : "w-20"}
      `}
    >
      {/* Cabecera del Menú (Logo y Botón Colapsar) */}
      <div className="flex h-20 items-center justify-between border-b border-slate-100 px-4">
        <Link
          to="/"
          className={`flex items-center justify-center overflow-hidden transition-all ${isExpanded ? "w-full opacity-100" : "w-0 opacity-0"
            }`}
        >
          <img src={logo} alt="Logo" className="w-full h-28 object-contain" />
        </Link>

        <button
          onClick={toggleMenu}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <Icon variante={isExpanded ? "chevron-left" : "chevron-right"} />
        </button>
      </div>

      {/* Lista de Navegación */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3.5 py-4 custom-scrollbar mt-10">
        {items.map((item, index) => (
          <MenuItemComponent key={index} item={item} isExpanded={isExpanded} />
        ))}
      </nav>
    </aside>
  );
};

// ---
// Componente de Item (Escritorio) con recursividad para Submenús
// ---
const MenuItemComponent = ({
  item,
  isExpanded,
}: {
  item: IMenuItem;
  isExpanded: boolean;
}) => {
  const location = useLocation(); // Corregido: Uso correcto de React Router
  const [isOpen, setIsOpen] = React.useState(false);

  const hasSubItems = item.submenus && item.submenus.length > 0;

  // Lógica para saber si está activo (incluyendo rutas hijas)
  const isActive =
    location.pathname === item.fc_ruta ||
    item.submenus?.some((subItem) => location.pathname === subItem.fc_ruta);

  return (
    <div className="group relative">
      <Link
        to={hasSubItems ? "#" : item.fc_ruta}
        onClick={(e) => {
          if (hasSubItems) {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        className={`flex items-center justify-between rounded-xl px-3 py-2 transition-colors
          ${isActive
            ? "bg-brand-50/60 font-semibold text-brand-600" // Clases cuando está activo
            : "font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900" // Clases inactivo
          }
        `}
      >
        <div className="flex items-center gap-3">
          <span className={`${isActive ? "text-brand-500" : "text-slate-400"}`}>
            <Icon variante={item.fc_icon} />
          </span>
          {isExpanded && <span className="text-sm">{item.fc_menu}</span>}
        </div>

        {isExpanded && hasSubItems && (
          <motion.span
            animate={{ rotate: isOpen ? 180 : 0 }}
            className={`transition-colors ${isActive ? "text-brand-600" : "text-slate-400"}`}
          >
            ▼
          </motion.span>
        )}
      </Link>

      {/* Submenús animado */}
      <AnimatePresence>
        {isExpanded && hasSubItems && isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="ml-2.5 mt-1.5 space-y-1 overflow-hidden border-l-2 border-slate-100 pl-3"
          >
            {item.submenus?.map((subItem, index) => (
              <MenuItemComponent key={index} item={subItem} isExpanded={isExpanded} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---
// Menú Móvil (Botón Flotante)
// ---
export const MenuMovil = ({ items }: IMenuProps): JSX.Element => {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();

  return (
    <Portal.Root>
      <div className="fixed bottom-4 right-4 z-[999] md:hidden">
        <Popover open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
          <PopoverTrigger asChild>
            <motion.div className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-brand-500 text-white shadow-xl shadow-brand-500/30 transition-transform active:scale-95">
              <Icon variante={isOpen ? "x" : "menu"} size={24} />
            </motion.div>
          </PopoverTrigger>

          <PopoverContent className="mb-2 mr-4 overflow-hidden rounded-2xl border border-slate-100 bg-white p-2 shadow-2xl">
            <motion.div className="flex w-56 flex-col space-y-1">
              {items.map((item, i) => (
                <Link
                  key={i}
                  to={item.fc_ruta || "#"}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-colors
                    ${location.pathname === item.fc_ruta
                      ? "bg-brand-50 text-brand-600 font-semibold"
                      : "text-slate-600 hover:bg-slate-50"
                    }
                  `}
                >
                  <Icon variante={item.fc_icon} />
                  <span className="text-sm">{item.fc_menu}</span>
                </Link>
              ))}
            </motion.div>
          </PopoverContent>
        </Popover>
      </div>
    </Portal.Root>
  );
};

export default Menu;
